import React, { useState, useMemo } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, Briefcase, ChevronUp, ChevronDown,
  PieChart as PieChartIcon, BarChart3, Target, Shield, Coins, Activity, ArrowUpDown, X, BellRing
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { getCategoryColor, getSectorColor, computeAllocationAlerts } from '../../utils/portfolio';
import { getUSMarketStatus, getUSMarketStatusBadge } from '../../utils/marketTime';
import { formatMoney, formatMoneyDetail, formatNumber, formatNumber4, formatPercent, isPrivacyMode as globalPrivacyMode } from '../../utils/formatters';
import StockLogo from '../common/StockLogo';
import { getSupportStatus, SUPPORT_COLORS } from '../simulator/BuyPlannerCard';

// Brand color mapping for accent bar & glow
const getBrandColor = (symbol) => {
  const colors = {
    BTC: '#F7931A', ETH: '#627EEA', SOL: '#9945FF', BNB: '#F0B90B', XRP: '#23292F',
    ADA: '#0033AD', DOGE: '#C2A633', AVAX: '#E84142', DOT: '#E6007A', LINK: '#2A5ADA',
    MATIC: '#8247E5', UNI: '#FF007A', NEAR: '#00C08B', OP: '#FF0420', ARB: '#28A0F0',
    META: '#0668E1', AAPL: '#555555', MSFT: '#00A4EF', GOOGL: '#4285F4', GOOG: '#4285F4',
    AMZN: '#FF9900', NVDA: '#76B900', TSLA: '#CC0000', NFLX: '#E50914', AMD: '#ED1C24',
    DIS: '#113CCF', V: '#1A1F71', MA: '#EB001B', JPM: '#003087', BAC: '#012169',
    WMT: '#0071CE', PG: '#003DA5', JNJ: '#D51900', KO: '#F40000', PEP: '#004B93',
    COST: '#E31837', NKE: '#111111', INTC: '#0071C5', CRM: '#00A1E0', PYPL: '#003087',
    QCOM: '#3253DC', UBER: '#000000', SQ: '#3E4348', SHOP: '#96BF48', SNOW: '#29B5E8',
    PLTR: '#101010', SOFI: '#6C3FC8', PL: '#6C3FC8', COIN: '#0052FF', HOOD: '#00C805',
    OKLO: '#00BFA5', SGOV: '#003DA5', SPYI: '#00BFA5', QQQI: '#00BFA5', QQQ: '#00BFA5',
    SPY: '#4CAF50', VOO: '#C41230', VTI: '#C41230', ARKK: '#FFFFFF', ARKG: '#00BFA5',
    GOLDTHB: '#FFD700', XAUUSD: '#FFD700',
  };
  return colors[symbol?.toUpperCase()] || null;
};

// Helper
const getAssetClassLabel = (assetClass, language) => {
  const labels = {
    'US Stock': { th: 'หุ้นสหรัฐ', en: 'US Stock' },
    Crypto: { th: 'คริปโต', en: 'Crypto' },
    ETF: { th: 'ETF', en: 'ETF' },
    Gold: { th: 'ทองคำ', en: 'Gold' }
  };
  return labels[assetClass]?.[language] || assetClass;
};

const DashboardTab = ({ summary, holdings, logos, formatCurrency, formatCurrencyDetail, currency, onToggleCurrency, onChangeCurrency, exchangeRate, language, darkMode, supportLevels, allocationLimits = {}, cashSummary, previousCloses = {}, todayCloses = {}, extendedPrices = {}, serverTimestamp, onNavigateToDeposit, onNavigateToAnalytics, onNavigateToCaps }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'currentValueUSD', direction: 'desc' });
  const [mobilePnlSort, setMobilePnlSort] = useState('default'); // 'default' | 'pnlDesc' | 'pnlAsc'
  const [dashboardClassFilter, setDashboardClassFilter] = useState(null); // null = show all, 'Gold' = filter
  const [expandedDashRow, setExpandedDashRow] = useState(null); // symbol for expanded mobile row
  const [showCashDetail, setShowCashDetail] = useState(false);
  const [showRealizedDetail, setShowRealizedDetail] = useState(false);
  const [showDividendDetail, setShowDividendDetail] = useState(false);

  const labels = language === 'en'
    ? {
      title: 'Portfolio Overview',
      subtitle: 'Summary of your portfolio value and returns',
      allocation: 'Portfolio Allocation',
      assetClassValue: 'Value by Asset Class',
      yourAssets: 'Your Holdings',
      assetDetails: 'Asset Details',
      noData: 'No data yet',
      noAssets: 'No assets yet',
      currentValue: 'Current Portfolio Value',
      cost: 'Cost',
      dailyChange: 'Daily Change',
      unrealizedPnL: 'Unrealized P&L',
      realizedPnL: 'Realized P&L',
      dividends: 'Dividends',
      assets: 'Assets',
      totalDividends: 'Total Dividends',
      assetCount: 'Asset Count',
      cashBalance: 'Cash Balance',
      items: 'items',
      quantity: 'Quantity',
      avgCost: 'Cost/Share',
      totalCost: 'Total Cost',
      currentPrice: 'Current Price',
      tableAsset: 'Asset',
      tableQuantity: 'Quantity',
      tableAvgCost: 'Average Cost',
      tableCurrentPrice: 'Current Price',
      tableValue: 'Value',
      tablePnL: 'P&L',
      noAssetData: 'No holding data yet',
      addTransactionToStart: 'Add transactions to get started',
      fxTitle: 'USD/THB Currency Tracker',
      fxAvgCost: 'Average Cost',
      fxCurrentRate: 'Current Rate',
      fxPnLPerUSD: 'P&L per USD',
      fxTotalDeposit: 'Total Deposited',
      fxTotalWithdraw: 'Total Withdrawn',
      fxNetUSD: 'Net USD Balance',
      fxUnrealized: 'Unrealized FX P&L',
      fxRealized: 'Realized FX P&L',
      fxTotalPnL: 'Total FX P&L',
      fxNoData: 'No deposit data yet',
      fxNoDataSub: 'Add deposit transactions to track FX cost basis',
      fxDeposit: 'Deposit',
      fxWithdraw: 'Withdraw',
      rebalanceTitle: 'Holdings over allocation cap',
      rebalanceCta: 'Review',
      overBadge: 'over'
    }
    : {
      title: 'ภาพรวมพอร์ตการลงทุน',
      subtitle: 'สรุปมูลค่าและผลตอบแทนของพอร์ตคุณ',
      allocation: 'สัดส่วนพอร์ต',
      assetClassValue: 'มูลค่าตามประเภทสินทรัพย์',
      yourAssets: 'สินทรัพย์ของคุณ',
      assetDetails: 'รายละเอียดสินทรัพย์',
      noData: 'ยังไม่มีข้อมูล',
      noAssets: 'ยังไม่มีสินทรัพย์',
      currentValue: 'มูลค่าพอร์ตปัจจุบัน',
      cost: 'ต้นทุน',
      dailyChange: 'เปลี่ยนแปลงรายวัน',
      unrealizedPnL: 'กำไร/ขาดทุน',
      realizedPnL: 'กำไรที่ขายแล้ว',
      dividends: 'เงินปันผล',
      assets: 'สินทรัพย์',
      totalDividends: 'เงินปันผลรวม',
      assetCount: 'จำนวนสินทรัพย์',
      cashBalance: 'เงินสดคงเหลือ',
      items: 'รายการ',
      quantity: 'จำนวน',
      avgCost: 'ต้นทุนต่อหุ้น',
      totalCost: 'ต้นทุนรวม',
      currentPrice: 'ราคาปัจจุบัน',
      tableAsset: 'สินทรัพย์',
      tableQuantity: 'จำนวน',
      tableAvgCost: 'ต้นทุนเฉลี่ย',
      tableCurrentPrice: 'ราคาปัจจุบัน',
      tableValue: 'มูลค่า',
      tablePnL: 'กำไร/ขาดทุน',
      noAssetData: 'ยังไม่มีข้อมูลสินทรัพย์',
      addTransactionToStart: 'เพิ่มธุรกรรมเพื่อเริ่มต้น',
      fxTitle: '💱 ต้นทุนเฉลี่ย USD/THB',
      fxAvgCost: 'ต้นทุนเฉลี่ย',
      fxCurrentRate: 'เรทปัจจุบัน',
      fxPnLPerUSD: 'กำไร/ขาดทุนต่อ USD',
      fxTotalDeposit: 'เงินฝากทั้งหมด',
      fxTotalWithdraw: 'เงินถอนทั้งหมด',
      fxNetUSD: 'USD คงเหลือ (ฝาก-ถอน)',
      fxUnrealized: 'กำไร/ขาดทุน FX ยังไม่ขาย',
      fxRealized: 'กำไร/ขาดทุน FX ขายแล้ว',
      fxTotalPnL: 'กำไร/ขาดทุน FX รวม',
      fxNoData: 'ยังไม่มีข้อมูลเงินฝาก',
      fxNoDataSub: 'เพิ่มธุรกรรมนำเงินเข้าเพื่อติดตามต้นทุนค่าเงิน',
      fxDeposit: 'ฝากเข้า',
      fxWithdraw: 'ถอนออก',
      rebalanceTitle: 'หุ้นเกินสัดส่วน%',
      rebalanceCta: 'ดูรายละเอียด',
      overBadge: 'เกิน'
    };

  // Allocation-cap overweight detection (base = invested assets, excludes cash)
  const allocAlerts = useMemo(
    () => computeAllocationAlerts(holdings, allocationLimits),
    [holdings, allocationLimits]
  );
  const overBySymbol = allocAlerts.bySymbol;
  const overweightList = allocAlerts.overweight;

  // คำนวณข้อมูลสรุป
  const dashboardData = useMemo(() => {
    const totalRealizedPnL = holdings.reduce((sum, h) => sum + (h.realizedPnLUSD || 0), 0);
    const totalDividends = holdings.reduce((sum, h) => sum + (h.totalDividendUSD || 0), 0);
    const activeHoldings = holdings.filter(h => h.quantity > 0);
    const profitableCount = activeHoldings.filter(h => h.pnlPercent > 0).length;
    const lossCount = activeHoldings.filter(h => h.pnlPercent < 0).length;

    // Daily Change: compare current prices vs previous close
    let dailyChangeUSD = 0;
    let yesterdayValue = 0;
    activeHoldings.forEach(h => {
      const prevClose = previousCloses[h.symbol];
      if (prevClose && prevClose > 0 && h.currentPriceUSD > 0) {
        const isTHB = h.currency === 'THB';
        const fxRate = (isTHB && exchangeRate > 0) ? exchangeRate : 1;
        const prevCloseUSD = prevClose / fxRate;
        dailyChangeUSD += (h.currentPriceUSD - prevCloseUSD) * h.quantity;
        yesterdayValue += prevCloseUSD * h.quantity;
      } else {
        yesterdayValue += h.currentValueUSD || 0;
      }
    });
    const dailyChangePercent = yesterdayValue > 0 ? (dailyChangeUSD / yesterdayValue) * 100 : 0;

    return {
      totalRealizedPnL,
      totalDividends,
      dailyChangeUSD,
      dailyChangePercent,
      activeCount: activeHoldings.length,
      profitableCount,
      lossCount,
      winRate: activeHoldings.length > 0 ? (profitableCount / activeHoldings.length) * 100 : 0
    };
  }, [holdings, previousCloses, exchangeRate]);

  const sortedHoldings = useMemo(() => {
    let sortableItems = [...holdings.filter(h => h.quantity > 0)];
    // Apply asset class filter from clicking allocation cards
    if (dashboardClassFilter) {
      sortableItems = sortableItems.filter(h => h.assetClass === dashboardClassFilter);
    }
    sortableItems.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortableItems;
  }, [holdings, sortConfig, dashboardClassFilter]);

  const mobileSortedHoldings = useMemo(() => {
    if (mobilePnlSort === 'default') return sortedHoldings;
    const sorted = [...sortedHoldings];
    sorted.sort((a, b) => {
      const pnlA = a.unrealizedPnLUSD || 0;
      const pnlB = b.unrealizedPnLUSD || 0;
      return mobilePnlSort === 'pnlDesc' ? pnlB - pnlA : pnlA - pnlB;
    });
    return sorted;
  }, [sortedHoldings, mobilePnlSort]);


  const requestSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown className="w-3 h-3 opacity-50" />;
    return sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const handleCurrencyChange = (nextCurrency) => {
    if (nextCurrency !== currency) {
      if (onChangeCurrency) {
        onChangeCurrency(nextCurrency);
      } else {
        onToggleCurrency();
      }
    }
  };

  const renderCurrencyToggle = ({ compact = false, className = '' } = {}) => (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      <div className="hero-currency-toggle" role="tablist" aria-label="Currency">
        <span className={`hero-currency-pill ${currency === 'USD' ? 'hero-currency-pill-usd' : 'hero-currency-pill-thb'}`}></span>
        {['USD', 'THB'].map((option) => {
          const isActive = currency === option;
          return (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={(e) => { e.stopPropagation(); handleCurrencyChange(option); }}
              className={`hero-currency-option ${isActive ? 'hero-currency-option-active' : 'hero-currency-option-inactive'}`}
            >
              {option}
            </button>
          );
        })}
      </div>
      {exchangeRate && !compact && (
        <div className="hero-currency-rate">
          1 USD ≈ ฿{exchangeRate.toFixed(2)}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4 md:space-y-6 animate-in fade-in duration-500">
      {/* Header - Hidden on mobile, shown in hero card */}
      <div className="hidden md:block">
        <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          {labels.title}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{labels.subtitle}</p>
      </div>

      {/* Main Value Card - Hero Section */}
      <div className="bg-gradient-to-br from-indigo-600 via-blue-700 to-purple-800 rounded-2xl md:rounded-[20px] p-4 md:p-4 lg:p-5 text-white shadow-xl relative transform-gpu isolate group overflow-hidden">

        {/* Dynamic Background — Animated Mesh Gradient Orbs */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl md:rounded-[24px] pointer-events-none">
          <div className="hero-orb-1 absolute -top-32 -right-32 w-96 h-96 bg-blue-400 rounded-full blur-[100px] opacity-40 group-hover:opacity-60 transition-opacity duration-1000 pointer-events-none"></div>
          <div className="hero-orb-2 absolute -bottom-32 -left-32 w-80 h-80 bg-purple-500 rounded-full blur-[100px] opacity-30 group-hover:opacity-50 transition-opacity duration-1000 pointer-events-none"></div>
          <div className="hero-orb-3 absolute top-1/2 left-1/3 w-64 h-64 bg-indigo-400 rounded-full blur-[80px] opacity-20 group-hover:opacity-40 transition-opacity duration-1000 pointer-events-none mix-blend-screen"></div>
        </div>

        {/* Aurora — slow rotating conic gradient */}
        <div className="absolute inset-0 overflow-hidden rounded-2xl md:rounded-[24px] pointer-events-none">
          <div className="hero-aurora"></div>
        </div>

        {/* Premium Noise Texture */}
        <div className="hero-noise rounded-2xl md:rounded-[24px]"></div>

        {/* Vignette for depth */}
        <div className="hero-vignette rounded-2xl md:rounded-[24px]"></div>

        {/* Shimmer Sweep */}
        <div className="hidden md:block hero-shimmer rounded-2xl md:rounded-[24px]"></div>

        {/* Currency Toggle Button - Mobile Only */}
        <div className="md:hidden absolute top-3 right-3 z-20">
          {renderCurrencyToggle({ compact: false, className: 'hero-toggle-mobile' })}
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden pt-8 relative z-10">
          <div className="flex flex-col mb-3">
            <p className="text-blue-100/90 text-[10px] font-bold uppercase tracking-[0.15em] mb-1">{labels.currentValue}</p>

            <h2 className="hero-main-value text-3xl font-black hero-value-strong tracking-tighter mb-1 text-white">
              {globalPrivacyMode ? '******' : formatCurrency(summary.totalValue)}
            </h2>


            {/* Mobile Metrics Row */}
            <div className="flex items-stretch gap-4 mt-1.5">
              <div className="flex flex-col justify-center">
                <div className="text-blue-100/60 text-[9px] font-semibold uppercase tracking-wider mb-0.5">{language === 'en' ? 'Total Return' : 'ผลตอบแทนรวม'}</div>
                <div className="flex items-baseline gap-1.5">
                  <span className={`text-[13px] font-black drop-shadow-sm ${summary.totalUnrealizedPnL >= 0 ? 'hero-value-positive hero-profit-pulse' : 'hero-value-negative hero-loss-pulse'}`}>
                    {summary.totalUnrealizedPnL >= 0 && !globalPrivacyMode ? '+' : ''}{globalPrivacyMode ? '******' : formatCurrency(summary.totalUnrealizedPnL)}
                  </span>
                  <span className={`text-[10px] font-black ${summary.totalUnrealizedPnL >= 0 ? 'hero-value-positive' : 'hero-value-negative'}`}>
                    ({formatPercent(summary.totalCost > 0 ? (summary.totalUnrealizedPnL / summary.totalCost) * 100 : 0)})
                  </span>
                </div>
              </div>

              {dashboardData.activeCount > 0 && (
                <>
                  <div className="w-px bg-white/10 my-1"></div>
                  <div className="flex flex-col justify-center">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Activity size={10} className={`${dashboardData.dailyChangeUSD >= 0 ? 'hero-value-positive' : 'hero-value-negative'} opacity-80`} />
                      <span className="text-blue-100/60 text-[9px] font-semibold uppercase tracking-wider">{language === 'en' ? '24H Change' : 'เปลี่ยนแปลง 24H'}</span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className={`text-[13px] font-black drop-shadow-sm ${dashboardData.dailyChangeUSD >= 0 ? 'hero-value-positive' : 'hero-value-negative'}`}>
                        {dashboardData.dailyChangeUSD >= 0 && !globalPrivacyMode ? '+' : ''}{globalPrivacyMode ? '******' : formatCurrency(dashboardData.dailyChangeUSD)}
                      </span>
                      <span className={`text-[10px] font-black ${dashboardData.dailyChangeUSD >= 0 ? 'hero-value-positive' : 'hero-value-negative'}`}>
                        ({dashboardData.dailyChangePercent >= 0 ? '+' : ''}{dashboardData.dailyChangePercent.toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="mt-3 pt-2 border-t border-white/10 flex items-center justify-between">
              <span className="text-blue-100/90 text-[10px] font-semibold uppercase tracking-wider">{language === 'en' ? 'Total Wealth' : 'ความมั่งคั่งรวม (สินทรัพย์ + เงินสด)'}</span>
              <span className="text-white text-[13px] font-bold drop-shadow-sm">{globalPrivacyMode ? '******' : formatCurrency(summary.totalWealth || 0)}</span>
            </div>
          </div>

          {/* Mobile Stat Cards - Gradient Premium */}
          <div className="grid grid-cols-3 gap-2">
            <div
              onClick={cashSummary?.depositCount === 0 && onNavigateToDeposit ? onNavigateToDeposit : (cashSummary?.depositCount > 0 ? () => setShowCashDetail(true) : undefined)}
              className={`hero-gradient-card hero-gradient-cash rounded-2xl p-2.5 flex flex-col justify-between relative overflow-hidden ${cashSummary?.depositCount > 0 || (cashSummary?.depositCount === 0 && onNavigateToDeposit) ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <DollarSign size={28} className="hero-watermark-icon" />
              <p className="text-white/80 text-[9px] font-bold uppercase tracking-wider mb-auto">{labels.cashBalance}</p>
              {cashSummary?.depositCount === 0 ? (
                <div>
                  <p className="text-sm font-black text-white/50 truncate tracking-tight">{globalPrivacyMode ? '******' : formatCurrency(0)}</p>
                  <p className="text-[7px] font-bold text-amber-200 whitespace-nowrap leading-tight mt-0.5 animate-pulse">{language === 'en' ? 'Tap to deposit' : 'แตะเพื่อเติมเงิน'}</p>
                </div>
              ) : (
                <p className={`text-sm font-black truncate tracking-tight ${cashSummary?.cashBalanceUSD < 0 ? 'text-red-200' : 'text-white'}`}>
                  {globalPrivacyMode ? '******' : formatCurrency(cashSummary?.cashBalanceUSD || 0)}
                </p>
              )}
            </div>

            <div onClick={() => setShowRealizedDetail(true)} className="hero-gradient-card hero-gradient-realized rounded-2xl p-2.5 flex flex-col justify-between relative overflow-hidden cursor-pointer">
              <TrendingUp size={28} className="hero-watermark-icon" />
              <p className="text-white/80 text-[9px] font-bold uppercase tracking-wider mb-auto">{labels.realizedPnL}</p>
              <p className={`text-sm font-black truncate tracking-tight text-white`}>
                {dashboardData.totalRealizedPnL >= 0 && !globalPrivacyMode ? '+' : ''}{globalPrivacyMode ? '******' : formatCurrency(dashboardData.totalRealizedPnL)}
              </p>
            </div>

            <div onClick={() => setShowDividendDetail(true)} className="hero-gradient-card hero-gradient-dividend rounded-2xl p-2.5 flex flex-col justify-between relative overflow-hidden cursor-pointer">
              <Coins size={28} className="hero-watermark-icon" />
              <p className="text-white/80 text-[9px] font-bold uppercase tracking-wider mb-auto">{labels.dividends}</p>
              <p className="text-sm font-black text-white truncate tracking-tight">
                {globalPrivacyMode ? '******' : formatCurrency(dashboardData.totalDividends)}
              </p>
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:block relative z-10">
          {/* Top Bar: LIVE indicator + Currency Toggle */}
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <div className="flex items-center gap-2.5">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 backdrop-blur-sm">
                <span className="hero-live-dot w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                <span className="text-[10px] font-bold tracking-[0.15em] text-emerald-300 uppercase">Live</span>
              </div>
              <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                <Briefcase size={11} className="text-blue-200" />
                <span className="text-[10px] font-bold tracking-wider text-blue-100/80 uppercase">
                  {dashboardData.activeCount} {language === 'en' ? 'Assets' : 'สินทรัพย์'}
                </span>
              </div>
              {dashboardData.activeCount > 0 && (
                <div className="hidden xl:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
                  <Target size={11} className="text-purple-200" />
                  <span className="text-[10px] font-bold tracking-wider text-blue-100/80 uppercase">
                    {language === 'en' ? 'Win Rate' : 'อัตราชนะ'} {dashboardData.winRate.toFixed(0)}%
                  </span>
                </div>
              )}
            </div>
            <div className="flex">
              {renderCurrencyToggle()}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-4 lg:gap-6 items-stretch">

            {/* Left - Portfolio Value & P&L (Integrated) */}
            <div className="flex flex-col justify-between">
              <div className="flex flex-col mb-4">
                <p className="text-blue-100/90 text-xs sm:text-sm font-semibold tracking-wider uppercase mb-1.5">{labels.currentValue}</p>

                <div className="flex items-end gap-3 mb-2 sm:mb-2">
                  <h2 className="hero-main-value text-4xl lg:text-4xl xl:text-5xl font-black hero-value-strong tracking-tighter leading-none text-white">
                    {globalPrivacyMode ? '******' : formatCurrency(summary.totalValue)}
                  </h2>
                </div>


                {/* Sleek Minimalist Metrics Row (Desktop) */}
                <div className="flex items-stretch gap-5 mt-2">
                  <div className="flex flex-col justify-center">
                    <div className="text-blue-100/60 text-[10px] lg:text-[11px] font-semibold uppercase tracking-wider mb-0.5">{language === 'en' ? 'Total Return' : 'ผลตอบแทนรวม'}</div>
                    <div className="flex items-baseline gap-2">
                      <span className={`text-sm lg:text-base font-black drop-shadow-sm ${summary.totalUnrealizedPnL >= 0 ? 'hero-value-positive hero-profit-pulse' : 'hero-value-negative hero-loss-pulse'}`}>
                        {summary.totalUnrealizedPnL >= 0 && !globalPrivacyMode ? '+' : ''}
                        {globalPrivacyMode ? '******' : formatCurrency(summary.totalUnrealizedPnL)}
                      </span>
                      <span className={`text-[11px] lg:text-xs font-black ${summary.totalUnrealizedPnL >= 0 ? 'hero-value-positive' : 'hero-value-negative'}`}>
                        ({formatPercent(summary.totalCost > 0 ? (summary.totalUnrealizedPnL / summary.totalCost) * 100 : 0)})
                      </span>
                    </div>
                  </div>

                  {dashboardData.activeCount > 0 && (
                    <>
                      <div className="w-px bg-white/10 my-1.5"></div>
                      <div className="flex flex-col justify-center">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Activity size={12} className={`${dashboardData.dailyChangeUSD >= 0 ? 'hero-value-positive' : 'hero-value-negative'} opacity-80`} />
                          <span className="text-blue-100/60 text-[10px] lg:text-[11px] font-semibold uppercase tracking-wider">{language === 'en' ? '24H Change' : 'เปลี่ยนแปลง 24H'}</span>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <span className={`text-sm lg:text-base font-black drop-shadow-sm ${dashboardData.dailyChangeUSD >= 0 ? 'hero-value-positive' : 'hero-value-negative'}`}>
                            {dashboardData.dailyChangeUSD >= 0 && !globalPrivacyMode ? '+' : ''}{globalPrivacyMode ? '******' : formatCurrency(dashboardData.dailyChangeUSD)}
                          </span>
                          <span className={`text-[11px] lg:text-xs font-black ${dashboardData.dailyChangeUSD >= 0 ? 'hero-value-positive' : 'hero-value-negative'}`}>
                            ({dashboardData.dailyChangePercent >= 0 ? '+' : ''}{dashboardData.dailyChangePercent.toFixed(2)}%)
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="mt-3 lg:mt-4 pt-2.5 lg:pt-3 border-t border-white/10 flex items-center justify-between pr-4 lg:pr-8">
                  <span className="text-blue-100/90 text-[10px] lg:text-[11px] font-semibold uppercase tracking-wider">{language === 'en' ? 'Total Wealth' : 'ความมั่งคั่งรวม (สินทรัพย์ + เงินสด)'}</span>
                  <span className="text-white text-sm lg:text-base font-bold drop-shadow-sm">{globalPrivacyMode ? '******' : formatCurrency(summary.totalWealth || 0)}</span>
                </div>
              </div>
            </div>

            {/* Right - Gradient Stat Cards */}
            <div className="flex flex-col justify-end">
              <div className="grid grid-cols-3 gap-2 xl:gap-3 mt-auto">

                {/* Cash Balance Card */}
                <div
                  onClick={cashSummary?.depositCount === 0 && onNavigateToDeposit ? onNavigateToDeposit : (cashSummary?.depositCount > 0 ? () => setShowCashDetail(true) : undefined)}
                  className={`hero-gradient-card hero-gradient-cash rounded-xl xl:rounded-2xl p-3 xl:p-3.5 flex flex-col justify-between relative overflow-hidden min-h-[72px] xl:min-h-[84px] ${cashSummary?.depositCount > 0 || (cashSummary?.depositCount === 0 && onNavigateToDeposit) ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <DollarSign size={28} className="hero-watermark-icon" />
                  <p className="text-white/80 text-[9px] xl:text-[10px] font-bold uppercase tracking-wider">{labels.cashBalance}</p>
                  {cashSummary?.depositCount === 0 ? (
                    <div>
                      <p className="text-sm xl:text-base font-black text-white/50 truncate tracking-tight">{globalPrivacyMode ? '******' : formatCurrency(0)}</p>
                      <p className="text-[8px] xl:text-[9px] font-bold text-amber-200 whitespace-nowrap leading-tight mt-0.5 animate-pulse">{language === 'en' ? 'Tap to add deposit' : 'แตะเพื่อเติมเงินเข้าพอร์ต'}</p>
                    </div>
                  ) : (
                    <p className={`text-sm xl:text-base font-black truncate tracking-tight ${cashSummary?.cashBalanceUSD < 0 ? 'text-red-200' : 'text-white'}`}>
                      {globalPrivacyMode ? '******' : formatCurrency(cashSummary?.cashBalanceUSD || 0)}
                    </p>
                  )}
                </div>

                {/* Realized P&L Card */}
                <div onClick={() => setShowRealizedDetail(true)} className="hero-gradient-card hero-gradient-realized rounded-xl xl:rounded-2xl p-3 xl:p-3.5 flex flex-col justify-between relative overflow-hidden min-h-[72px] xl:min-h-[84px] cursor-pointer">
                  <TrendingUp size={28} className="hero-watermark-icon" />
                  <p className="text-white/80 text-[9px] xl:text-[10px] font-bold uppercase tracking-wider">{labels.realizedPnL}</p>
                  <p className="text-sm xl:text-base font-black text-white truncate tracking-tight">
                    {dashboardData.totalRealizedPnL >= 0 && !globalPrivacyMode ? '+' : ''}{globalPrivacyMode ? '******' : formatCurrency(dashboardData.totalRealizedPnL)}
                  </p>
                </div>

                {/* Dividend Card */}
                <div onClick={() => setShowDividendDetail(true)} className="hero-gradient-card hero-gradient-dividend rounded-xl xl:rounded-2xl p-3 xl:p-3.5 flex flex-col justify-between relative overflow-hidden min-h-[72px] xl:min-h-[84px] cursor-pointer">
                  <Coins size={28} className="hero-watermark-icon" />
                  <p className="text-white/80 text-[9px] xl:text-[10px] font-bold uppercase tracking-wider">{labels.totalDividends}</p>
                  <p className="text-sm xl:text-base font-black text-white truncate tracking-tight">
                    {globalPrivacyMode ? '******' : formatCurrency(dashboardData.totalDividends)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Premium Unified Asset Allocation Dashboard */}
      <div className={`rounded-[20px] md:rounded-[24px] shadow-sm overflow-hidden relative transform-gpu isolate ${darkMode ? 'bg-slate-900/50 border border-slate-800' : 'bg-slate-50/80 border border-slate-100/60'}`}>
        {/* Refined Subtle Background Effects */}
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-indigo-100/30 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-emerald-50/30 rounded-full blur-3xl pointer-events-none"></div>

        <div className="p-3 sm:p-4 lg:p-4 flex flex-col lg:flex-row gap-4 sm:gap-4 lg:gap-5 relative z-10 max-w-5xl mx-auto">

          {/* Left Side: Pie Chart Focus */}
          <div className="flex-1 lg:max-w-xs flex flex-col items-center justify-center lg:self-stretch">
            <div className="w-full flex items-center justify-between lg:justify-center gap-2.5 mb-2 sm:mb-3">
              <div className="flex items-center gap-2.5">
                <div className={`p-1.5 rounded-lg shadow-sm border ${darkMode ? 'bg-indigo-900/50 text-indigo-400 border-indigo-700/50' : 'bg-white text-indigo-500 border-indigo-50'}`}>
                  <PieChartIcon size={14} className="drop-shadow-sm" />
                </div>
                <h3 className={`text-sm sm:text-base font-black tracking-tight ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{labels.allocation}</h3>
              </div>

              {/* Mobile-only Asset Count Indicator */}
              <div className={`lg:hidden flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${darkMode ? 'bg-slate-800 text-slate-300 border border-slate-700' : 'bg-slate-100/80 text-slate-500 border border-slate-200/60'}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-indigo-400' : 'bg-indigo-500'}`}></div>
                {dashboardData.activeCount} {labels.assets}
              </div>
            </div>

            {summary.chartData.length > 0 ? (
              <div className="w-full h-[120px] sm:h-[180px] lg:h-[180px] mt-1 sm:mt-0 group">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={summary.chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius="60%"
                      outerRadius="90%"
                      paddingAngle={4}
                      dataKey="value"
                      stroke="none"
                      cornerRadius={6}
                      className="transition-all duration-300"
                    >
                      {summary.chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={getCategoryColor(entry.name)}
                          className="hover:opacity-80 transition-opacity duration-300 cursor-pointer outline-none"
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value, _name, item) => [formatMoney(value), getAssetClassLabel(item?.payload?.name, language)]}
                      contentStyle={{ borderRadius: '16px', border: '1px solid #f1f5f9', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1)' }}
                      itemStyle={{ fontWeight: 'bold' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 lg:h-full text-slate-400 mt-12">
                <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center mb-4 border border-slate-100 shadow-inner">
                  <PieChartIcon size={32} className="text-slate-300" />
                </div>
                <p className="font-semibold">{labels.noData}</p>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent opacity-50"></div>

          {/* Right Side: Data Cards as Legend */}
          <div className="flex-[2] flex flex-col">
            <div className={`hidden lg:flex items-center justify-between mb-3`}>
              <h3 className={`flex text-sm font-bold items-center gap-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                <Briefcase className={darkMode ? 'text-slate-500' : 'text-slate-400'} size={14} />
                {labels.assetClassValue}
              </h3>
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${darkMode ? 'bg-slate-800 text-slate-300 border border-slate-700' : 'bg-slate-100/80 text-slate-500 border border-slate-200/60'}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${darkMode ? 'bg-indigo-400' : 'bg-indigo-500'}`}></div>
                {dashboardData.activeCount} {labels.assets}
              </div>
            </div>

            <div className="flex flex-col gap-0 flex-1 content-center">
              {summary.chartData.length > 0 ? (
                <div className={`rounded-xl overflow-hidden border ${darkMode ? 'bg-slate-800/50 border-slate-700/50' : 'bg-white border-slate-100'}`}>
                  {[...summary.chartData].sort((a, b) => b.value - a.value).map((item, i, arr) => {
                    const isCash = item.name === 'Cash';
                    const assetHoldings = holdings.filter(h => h.assetClass === item.name && h.quantity > 0);
                    const totalPnL = isCash ? (cashSummary?.unrealizedFxPnL || 0) : assetHoldings.reduce((sum, h) => sum + (h.unrealizedPnLUSD || 0), 0);
                    const totalCost = isCash ? (item.value) : assetHoldings.reduce((sum, h) => sum + (h.totalCostUSD || 0), 0);
                    const pnlPercent = totalCost > 0 ? (totalPnL / totalCost) * 100 : 0;
                    const displayItemCount = isCash ? (cashSummary?.depositCount || 0) : assetHoldings.length;
                    const isSelected = dashboardClassFilter === item.name;

                    return (
                      <div
                        key={i}
                        onClick={() => setDashboardClassFilter(prev => prev === item.name ? null : item.name)}
                        className={`relative cursor-pointer transition-all duration-200 ${i < arr.length - 1 ? (darkMode ? 'border-b border-slate-700/40' : 'border-b border-slate-100') : ''} ${isSelected ? (darkMode ? 'bg-slate-700/60' : 'bg-blue-50/60') : ''}`}
                      >
                        {/* Left color accent bar */}
                        <div className="absolute left-0 top-0 bottom-0 w-1 rounded-r-full" style={{ backgroundColor: getCategoryColor(item.name), opacity: isSelected ? 1 : 0.6 }}></div>

                        <div className="pl-4 pr-3 py-3 sm:py-3.5">
                          <div className="flex items-center justify-between gap-3">
                            {/* Left: Color dot + Name + Allocation% */}
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className="w-3 h-3 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: getCategoryColor(item.name) }}></div>
                              <div className="flex flex-col min-w-0">
                                <span className={`font-bold text-[13px] leading-tight ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{getAssetClassLabel(item.name, language)}</span>
                                <span className={`text-[11px] font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                  {item.percent.toFixed(1)}%{' · '}{displayItemCount} {language === 'en' ? (isCash ? 'deposits' : 'assets') : (isCash ? 'ยอดฝาก' : 'รายการ')}
                                </span>
                              </div>
                            </div>

                            {/* Center: Value */}
                            <span className={`font-black text-[14px] tabular-nums tracking-tight shrink-0 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                              {globalPrivacyMode ? '******' : formatCurrency(item.value)}
                            </span>

                            {/* Right: P&L USD + P&L% stacked */}
                            <div className="flex flex-col items-end shrink-0 min-w-[90px]">
                              {!isCash ? (
                                <>
                                  <span className={`text-[12px] font-bold tabular-nums leading-tight ${totalPnL >= 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-rose-400' : 'text-rose-600')}`}>
                                    {globalPrivacyMode ? '******' : `${totalPnL >= 0 ? '+' : ''}${formatCurrency(totalPnL)}`}
                                  </span>
                                  <span className={`text-[10px] font-semibold tabular-nums ${totalPnL >= 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-rose-400' : 'text-rose-600')}`}>
                                    ({formatPercent(pnlPercent)})
                                  </span>
                                </>
                              ) : (
                                <span className={`text-[10px] font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>—</span>
                              )}
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className={`h-1.5 rounded-full overflow-hidden mt-2.5 ${darkMode ? 'bg-slate-700/60' : 'bg-slate-100'}`}>
                            <div
                              className="h-full rounded-full transition-all duration-700 ease-out"
                              style={{ width: `${item.percent}%`, backgroundColor: getCategoryColor(item.name) }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`rounded-2xl p-12 text-center border border-dashed ${darkMode ? 'bg-slate-800/50 border-slate-700 text-slate-500' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>
                  <Briefcase size={48} className={`mx-auto mb-4 ${darkMode ? 'text-slate-600' : 'text-slate-300'}`} />
                  <p className="font-medium">{labels.noAssets}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Holdings - Mobile Compact List */}
      <div className="md:hidden">
        <div className={`flex items-center justify-between px-1 py-2.5`}>
          <h3 className={`text-sm font-bold flex items-center gap-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
            {labels.yourAssets}
            {dashboardClassFilter && (
              <button onClick={() => setDashboardClassFilter(null)} className={`flex items-center gap-1 ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-700 border border-blue-200'} hover:opacity-80 transition-all`}>
                {getAssetClassLabel(dashboardClassFilter, language)} ✕
              </button>
            )}
          </h3>
          <button
            onClick={() => setMobilePnlSort(prev => prev === 'default' ? 'pnlDesc' : prev === 'pnlDesc' ? 'pnlAsc' : 'default')}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold transition-all duration-200 border ${mobilePnlSort === 'default'
              ? (darkMode ? 'bg-slate-800 text-slate-400 border-slate-700' : 'bg-slate-100 text-slate-500 border-slate-200')
              : mobilePnlSort === 'pnlDesc'
                ? (darkMode ? 'bg-emerald-900/30 text-emerald-400 border-emerald-800' : 'bg-emerald-50 text-emerald-600 border-emerald-200')
                : (darkMode ? 'bg-rose-900/30 text-rose-400 border-rose-800' : 'bg-rose-50 text-rose-600 border-rose-200')
              }`}
          >
            {mobilePnlSort === 'default' ? (
              <><ArrowUpDown size={11} /> {language === 'en' ? 'P&L' : 'กำไร/ขาดทุน'}</>
            ) : mobilePnlSort === 'pnlDesc' ? (
              <><TrendingUp size={11} /> {language === 'en' ? 'Profit ↓' : 'กำไรมาก↓'}</>
            ) : (
              <><TrendingDown size={11} /> {language === 'en' ? 'Loss ↓' : 'ขาดทุนมาก↓'}</>
            )}
          </button>
        </div>

        <div className="space-y-1.5">
        {mobileSortedHoldings.length > 0 ? (
          mobileSortedHoldings.map((item, i) => {
            const portPercent = summary.totalValue > 0 ? (item.currentValueUSD / summary.totalValue) * 100 : 0;
            const isExpanded = expandedDashRow === item.symbol;
            const hasSupportAlert = (() => {
              const sl = supportLevels && supportLevels[item.symbol];
              if (!sl) return false;
              const levels = [sl.s1, sl.s2, sl.s3, sl.s4, sl.s5, sl.s6, sl.s7].filter(v => v > 0);
              return levels.some(lv => { const st = getSupportStatus(item.currentPriceUSD, lv); return st && (st.type === 'below' || st.type === 'near'); });
            })();

            const brandColor = getBrandColor(item.symbol) || (item.unrealizedPnLUSD >= 0 ? '#10b981' : '#f43f5e');

            return (
              <div
                key={i}
                className={`rounded-2xl border-l-[4px] transition-all duration-300 ${
                  darkMode
                    ? `md:hover:brightness-110`
                    : `md:hover:bg-slate-50 ${isExpanded ? 'bg-slate-50' : 'bg-white'}`
                }`}
                style={{
                  borderLeftColor: brandColor,
                  background: darkMode
                    ? (isExpanded
                        ? 'linear-gradient(135deg, #0d1830 0%, #0a1322 100%)'
                        : 'linear-gradient(135deg, #0b1426 0%, #080f1d 100%)')
                    : undefined,
                  boxShadow: darkMode
                    ? (isExpanded
                        ? 'inset 0 1px 0 0 rgba(255,255,255,0.18), inset 0 0 0 1px rgba(255,255,255,0.06), inset 0 -12px 24px -14px rgba(0,0,0,0.7), 0 6px 16px -6px rgba(0,0,0,0.6)'
                        : 'inset 0 1px 0 0 rgba(255,255,255,0.14), inset 0 0 0 1px rgba(255,255,255,0.04), inset 0 -10px 20px -14px rgba(0,0,0,0.6), 0 4px 12px -6px rgba(0,0,0,0.5)')
                    : (isExpanded
                        ? 'inset 0 1px 0 0 rgba(255,255,255,0.9), inset 0 0 0 1px rgba(15,23,42,0.06), 0 6px 16px -6px rgba(15,23,42,0.18)'
                        : 'inset 0 1px 0 0 rgba(255,255,255,0.8), inset 0 0 0 1px rgba(15,23,42,0.05), 0 4px 12px -6px rgba(15,23,42,0.12)'),
                }}
              >
                {/* Compact Row */}
                <div
                  onClick={() => setExpandedDashRow(prev => prev === item.symbol ? null : item.symbol)}
                  className="grid grid-cols-[1fr_auto_1fr] items-center gap-1 px-3 py-3.5 cursor-pointer"
                >
                  {/* Left: Logo + Symbol */}
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`w-9 h-9 flex items-center justify-center shrink-0 overflow-hidden rounded-xl ${logos && logos[item.symbol] ? '' : 'shadow-sm'}`}>
                      {logos && logos[item.symbol] ? (
                        <img src={logos[item.symbol]} alt={item.symbol} className="w-9 h-9 rounded-xl object-contain drop-shadow-sm dark:drop-shadow-[0_0_3px_rgba(255,255,255,0.3)] dark:brightness-110" onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                      ) : null}
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-[10px] shadow-sm" style={{ backgroundColor: getCategoryColor(item.assetClass), display: logos && logos[item.symbol] ? 'none' : 'flex' }}>
                        {item.symbol.substring(0, 2).toUpperCase()}
                      </div>
                    </div>
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`font-bold text-[14px] leading-tight ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{item.symbol}</span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className={`text-[10px] font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{portPercent.toFixed(1)}%</span>
                        {overBySymbol[item.symbol]?.isOver && (
                          <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full border shadow-sm transition-all ${
                            darkMode ? 'bg-rose-950/60 border-rose-800/50 text-rose-300' : 'bg-white border-rose-200/60 text-rose-600'
                          }`}>
                            <Target size={8} className="text-rose-500" />
                            {labels.overBadge} +{overBySymbol[item.symbol].overPct.toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Middle: Value — centered */}
                  <div className="text-center px-2">
                    <div className={`font-black text-[14px] tracking-tight tabular-nums ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                      {globalPrivacyMode ? '******' : (item.currency === 'THB' && item.currentValueNative
                        ? `฿${item.currentValueNative.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                        : formatCurrency(item.currentValueUSD))}
                    </div>
                  </div>

                  {/* Right: P&L — right-aligned */}
                  <div className="text-right">
                    <div className={`text-[13px] font-bold tracking-tight leading-tight tabular-nums ${item.unrealizedPnLUSD >= 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-rose-400' : 'text-rose-600')}`}>
                      {globalPrivacyMode ? '******' : (item.currency === 'THB' && item.unrealizedPnLNative !== undefined
                        ? `${item.unrealizedPnLNative >= 0 ? '+' : ''}฿${Math.abs(item.unrealizedPnLNative).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                        : `${item.unrealizedPnLUSD >= 0 ? '+' : ''}${formatCurrency(item.unrealizedPnLUSD)}`
                      )}
                    </div>
                    <div className={`text-[10px] font-bold tabular-nums ${item.unrealizedPnLUSD >= 0 ? (darkMode ? 'text-emerald-400/70' : 'text-emerald-600') : (darkMode ? 'text-rose-400/70' : 'text-rose-600')}`}>
                      ({formatPercent(item.pnlPercent)})
                    </div>
                  </div>
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className={`px-3 pb-3`}>
                    {/* Support Alerts */}
                    {(() => {
                      const sl = supportLevels && supportLevels[item.symbol];
                      if (!sl) return null;
                      const alerts = [{ l: 1, p: sl.s1, c: SUPPORT_COLORS[0] }, { l: 2, p: sl.s2, c: SUPPORT_COLORS[1] }, { l: 3, p: sl.s3, c: SUPPORT_COLORS[2] }, { l: 4, p: sl.s4, c: SUPPORT_COLORS[3] }, { l: 5, p: sl.s5, c: SUPPORT_COLORS[4] }, { l: 6, p: sl.s6, c: SUPPORT_COLORS[5] }, { l: 7, p: sl.s7, c: SUPPORT_COLORS[6] }]
                        .filter(a => a.p > 0)
                        .map(a => ({ ...a, status: getSupportStatus(item.currentPriceUSD, a.p) }))
                        .filter(a => a.status && (a.status.type === 'below' || a.status.type === 'near'));
                      if (alerts.length === 0) return null;
                      return (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {alerts.map(a => (
                            <span key={a.l} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-bold ${a.status.type === 'below' ? `bg-gradient-to-r ${a.c.gradient} text-white` : `${a.c.bg} ${a.c.text} border ${a.c.border}`}`}>
                              {a.status.type === 'below' ? '🔥' : '⚠️'} S{a.l}: ${a.p.toFixed(2)}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                    {/* Divider line */}
                    <div className={`h-px mb-2.5 ${darkMode ? 'bg-slate-700/60' : 'bg-slate-200'}`}></div>
                    <div className="flex items-start justify-between">
                      {/* จำนวน */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-[9px] font-semibold uppercase tracking-wider mb-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{labels.quantity}</p>
                        <p className={`text-[11px] font-bold tabular-nums ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{formatNumber(item.quantity)}{item.symbol === 'GOLDTHB' ? ` ${language === 'en' ? 'baht' : 'บาท'}` : ''}</p>
                        {item.symbol === 'GOLDTHB' && (
                          <p className={`text-[8px] mt-px ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>({(item.quantity * 15.244).toFixed(2)} {language === 'en' ? 'g' : 'ก.'})</p>
                        )}
                      </div>
                      {/* Divider */}
                      <div className={`w-px self-stretch mx-1.5 ${darkMode ? 'bg-slate-700/60' : 'bg-slate-200'}`}></div>
                      {/* ต้นทุนต่อหุ้น */}
                      <div className="flex-1 min-w-0 text-center">
                        <p className={`text-[9px] font-semibold uppercase tracking-wider mb-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{labels.avgCost}</p>
                        <p className={`text-[11px] font-bold tabular-nums ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          {item.currency === 'THB' && item.avgCostNative
                            ? `฿${item.avgCostNative.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                            : formatCurrencyDetail(item.avgCostUSD)}
                        </p>
                      </div>
                      {/* Divider */}
                      <div className={`w-px self-stretch mx-1.5 ${darkMode ? 'bg-slate-700/60' : 'bg-slate-200'}`}></div>
                      {/* ต้นทุนรวม */}
                      <div className="flex-1 min-w-0 text-center">
                        <p className={`text-[9px] font-semibold uppercase tracking-wider mb-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{labels.totalCost}</p>
                        <p className={`text-[11px] font-bold tabular-nums ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                          {globalPrivacyMode ? '****' : (item.currency === 'THB' && item.avgCostNative
                            ? `฿${(item.avgCostNative * item.quantity).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                            : formatCurrency(item.totalCostUSD))}
                        </p>
                      </div>
                      {/* Divider */}
                      <div className={`w-px self-stretch mx-1.5 ${darkMode ? 'bg-slate-700/60' : 'bg-slate-200'}`}></div>
                      {/* ราคาปัจจุบัน */}
                      <div className="flex-1 min-w-0 text-right">
                        <p className={`text-[9px] font-semibold uppercase tracking-wider mb-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{labels.currentPrice}</p>
                        <p className={`text-[11px] font-bold tabular-nums ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                          {item.currency === 'THB' && item.currentPriceNative
                            ? `฿${item.currentPriceNative.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                            : formatCurrencyDetail(item.currentPriceUSD)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className={`p-8 text-center rounded-2xl border ${darkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-100'}`}>
            <Briefcase className={`w-10 h-10 mx-auto mb-2 ${darkMode ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{labels.noAssets}</p>
          </div>
        )}
        </div>
      </div>

      {/* Holdings - Desktop Card List */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between mb-2 px-1">
          <h3 className={`text-base font-bold flex items-center gap-2 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            {labels.assetDetails}
            {dashboardClassFilter && (
              <button onClick={() => setDashboardClassFilter(null)} className={`flex items-center gap-1 ml-2 text-xs font-bold px-2.5 py-1 rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-300 border border-blue-700 hover:bg-blue-800/60' : 'bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200'} transition-all`}>
                {getAssetClassLabel(dashboardClassFilter, language)} ✕
              </button>
            )}
          </h3>
        </div>

        {/* Sort Header */}
        <div className={`flex items-center gap-2 px-4 py-2 mb-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          <div className="w-[190px] shrink-0 cursor-pointer hover:text-slate-300 transition-colors flex items-center gap-1.5" onClick={() => requestSort('symbol')}>
            {labels.tableAsset} <SortIcon columnKey="symbol" />
          </div>
          <div className="flex-1 text-right cursor-pointer hover:text-slate-300 transition-colors flex items-center justify-end gap-1.5" onClick={() => requestSort('quantity')}>
            {labels.tableQuantity} <SortIcon columnKey="quantity" />
          </div>
          <div className="flex-1 text-right cursor-pointer hover:text-slate-300 transition-colors flex items-center justify-end gap-1.5" onClick={() => requestSort('avgCostUSD')}>
            {labels.tableAvgCost} <SortIcon columnKey="avgCostUSD" />
          </div>
          <div className="flex-1 text-right cursor-pointer hover:text-slate-300 transition-colors flex items-center justify-end gap-1.5" onClick={() => requestSort('currentPriceUSD')}>
            {labels.tableCurrentPrice} <SortIcon columnKey="currentPriceUSD" />
          </div>
          <div className={`flex-1 text-right cursor-pointer transition-colors flex items-center justify-end gap-1.5 ${darkMode ? 'text-blue-400/70 hover:text-blue-300' : 'text-blue-500/70 hover:text-blue-600'}`} onClick={() => requestSort('currentValueUSD')}>
            {labels.tableValue} <SortIcon columnKey="currentValueUSD" />
          </div>
          <div className="w-[120px] shrink-0 text-right cursor-pointer hover:text-slate-300 transition-colors flex items-center justify-end gap-1.5" onClick={() => requestSort('unrealizedPnLUSD')}>
            {labels.tablePnL} <SortIcon columnKey="unrealizedPnLUSD" />
          </div>
        </div>

        {/* Card Rows */}
        <div className="space-y-1.5">
          {sortedHoldings.map((item, i) => {
            const portPercent = summary.totalValue > 0 ? (item.currentValueUSD / summary.totalValue) * 100 : 0;
            const desktopBrandColor = getBrandColor(item.symbol) || (item.unrealizedPnLUSD >= 0 ? '#10b981' : '#f43f5e');
            const hasSupportAlert = (() => {
              const sl = supportLevels && supportLevels[item.symbol];
              if (!sl) return false;
              const levels = [sl.s1, sl.s2, sl.s3, sl.s4, sl.s5, sl.s6, sl.s7].filter(v => v > 0);
              return levels.some(lv => { const st = getSupportStatus(item.currentPriceUSD, lv); return st && (st.type === 'below' || st.type === 'near'); });
            })();

            return (
              <div
                key={i}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-l-[3px] transition-all duration-300 ${
                  darkMode
                    ? 'hover:brightness-110'
                    : 'hover:bg-slate-50'
                }`}
                style={{
                  borderLeftColor: desktopBrandColor,
                  background: darkMode
                    ? 'linear-gradient(135deg, #0b1426 0%, #080f1d 100%)'
                    : undefined,
                  boxShadow: darkMode
                    ? 'inset 0 1px 0 0 rgba(255,255,255,0.14), inset 0 0 0 1px rgba(255,255,255,0.04), inset 0 -10px 20px -14px rgba(0,0,0,0.6), 0 4px 12px -6px rgba(0,0,0,0.5)'
                    : 'inset 0 1px 0 0 rgba(255,255,255,0.8), inset 0 0 0 1px rgba(15,23,42,0.05), 0 4px 12px -6px rgba(15,23,42,0.12)',
                }}
              >
                {/* Asset Info */}
                <div className="w-[190px] shrink-0 flex items-center gap-2.5">
                  <div className={`w-9 h-9 flex items-center justify-center shrink-0 overflow-hidden rounded-lg ${logos && logos[item.symbol] ? '' : 'shadow-sm'}`}>
                    {logos && logos[item.symbol] ? (
                      <img src={logos[item.symbol]} alt={item.symbol} className="w-9 h-9 rounded-lg object-contain drop-shadow-sm dark:drop-shadow-[0_0_3px_rgba(255,255,255,0.3)] dark:brightness-110" onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }} />
                    ) : null}
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-xs shadow-md" style={{ backgroundColor: getCategoryColor(item.assetClass), display: logos && logos[item.symbol] ? 'none' : 'flex' }}>
                      {item.symbol.substring(0, 2).toUpperCase()}
                    </div>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`font-bold text-[13px] leading-tight ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>{item.symbol}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                      <span className={`text-[10px] font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{getAssetClassLabel(item.assetClass, language)}</span>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${darkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-blue-50 text-blue-600'}`}>
                        {portPercent.toFixed(1)}%
                      </span>
                      {overBySymbol[item.symbol]?.isOver && (
                        <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded border shadow-sm transition-all ${
                          darkMode ? 'bg-rose-950/60 border-rose-800/50 text-rose-300' : 'bg-white border-rose-200/60 text-rose-600'
                        }`}>
                          <Target size={8} className="text-rose-500" />
                          {labels.overBadge} +{overBySymbol[item.symbol].overPct.toFixed(1)}%
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quantity */}
                <div className="flex-1 text-right">
                  <span className={`font-mono font-semibold text-[12px] ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>{formatNumber(item.quantity)}{item.symbol === 'GOLDTHB' ? ` ${language === 'en' ? 'baht' : 'บาท'}` : ''}</span>
                  {item.symbol === 'GOLDTHB' && (
                    <div className={`text-[10px] font-mono ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>({(item.quantity * 15.244).toFixed(2)} {language === 'en' ? 'g' : 'ก.'})</div>
                  )}
                </div>

                {/* Avg Cost */}
                <div className="flex-1 text-right">
                  <span className={`font-mono text-[12px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {item.currency === 'THB' && item.avgCostNative
                      ? `฿${item.avgCostNative.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                      : formatCurrencyDetail(item.avgCostUSD)}
                  </span>
                </div>

                {/* Current Price */}
                <div className="flex-1 text-right">
                  <span className={`font-mono font-semibold text-[12px] ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    {item.currency === 'THB' && item.currentPriceNative
                      ? `฿${item.currentPriceNative.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                      : formatCurrencyDetail(item.currentPriceUSD)}
                  </span>
                </div>

                {/* Value */}
                <div className="flex-1 text-right">
                  <span className={`font-mono font-bold text-[12px] ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {item.currency === 'THB' && item.currentValueNative
                      ? `฿${item.currentValueNative.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                      : formatCurrency(item.currentValueUSD)}
                  </span>
                </div>

                {/* P&L */}
                <div className="w-[120px] shrink-0 text-right">
                  <div className={`font-mono font-bold text-[12px] ${item.unrealizedPnLUSD >= 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-rose-400' : 'text-rose-600')}`}>
                    {item.currency === 'THB' && item.unrealizedPnLNative !== undefined
                      ? `${item.unrealizedPnLNative >= 0 && !globalPrivacyMode ? '+' : ''}฿${Math.abs(item.unrealizedPnLNative).toLocaleString('en-US', { maximumFractionDigits: 0 })}`
                      : `${item.unrealizedPnLUSD >= 0 && !globalPrivacyMode ? '+' : ''}${formatCurrency(item.unrealizedPnLUSD)}`
                    }
                  </div>
                  <div className={`text-[10px] font-semibold mt-0.5 ${item.unrealizedPnLUSD >= 0 ? (darkMode ? 'text-emerald-400/70' : 'text-emerald-600') : (darkMode ? 'text-rose-400/70' : 'text-rose-600')}`}>
                    {formatPercent(item.pnlPercent)}
                  </div>
                </div>
              </div>
            );
          })}
          {sortedHoldings.length === 0 && (
            <div className={`p-16 text-center rounded-2xl border ${darkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-100'}`}>
              <Briefcase className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-slate-600' : 'text-slate-300'}`} />
              <p className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{labels.noAssetData}</p>
              <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{labels.addTransactionToStart}</p>
            </div>
          )}
        </div>
      </div>
      {/* Cash Detail Popup */}
      {showCashDetail && cashSummary && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setShowCashDetail(false)}>
          <div className="absolute inset-0 bg-black/10 backdrop-blur-[3px]" />
          <div
            className={`relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl ${darkMode ? 'bg-gradient-to-br from-slate-800 via-slate-800 to-indigo-900/40 border border-slate-700/60' : 'bg-gradient-to-br from-white via-white to-indigo-50/50 border border-slate-200'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-5 pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`p-2 rounded-xl ${darkMode ? 'bg-gradient-to-br from-teal-600/30 to-cyan-600/20 text-cyan-400 border border-teal-500/30' : 'bg-gradient-to-br from-teal-50 to-cyan-50 text-teal-600 border border-teal-100'}`}>
                    <DollarSign size={18} />
                  </div>
                  <div>
                    <h3 className={`text-sm font-black tracking-tight ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                      {language === 'en' ? 'USD/THB Currency Tracker' : 'USD/THB Currency Tracker'}
                    </h3>
                    <p className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      {language === 'en' ? 'Cost basis & FX gains' : 'ติดตามต้นทุนและกำไร/ขาดทุนจากอัตราแลกเปลี่ยน'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowCashDetail(false)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${darkMode ? 'bg-slate-700/60 text-slate-400 hover:text-white' : 'bg-slate-100 text-slate-400 hover:text-slate-700'}`}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Content Grid */}
            <div className="px-5 pb-5 grid grid-cols-2 gap-2.5">
              {/* Avg Cost */}
              <div className={`rounded-2xl p-3.5 border ${darkMode ? 'bg-slate-700/40 border-slate-600/50' : 'bg-white/80 border-slate-200/60 shadow-sm'}`}>
                <div className={`text-[9px] font-bold uppercase tracking-wider mb-1.5 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  {language === 'en' ? 'Avg Cost' : 'ต้นทุนเฉลี่ย'}
                </div>
                <div className={`text-xl font-black tabular-nums tracking-tight ${darkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                  ฿{cashSummary.avgCostRate.toFixed(2)}
                </div>
              </div>

              {/* Current Rate */}
              <div className={`rounded-2xl p-3.5 border ${darkMode ? 'bg-slate-700/40 border-slate-600/50' : 'bg-white/80 border-slate-200/60 shadow-sm'}`}>
                <div className={`text-[9px] font-bold uppercase tracking-wider mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'en' ? 'Current Rate' : 'เรทปัจจุบัน'}
                </div>
                <div className={`text-xl font-black tabular-nums tracking-tight flex items-center gap-1.5 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  ฿{exchangeRate ? exchangeRate.toFixed(2) : '-'}
                  {exchangeRate && cashSummary.avgCostRate > 0 && (
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-md ${
                      exchangeRate > cashSummary.avgCostRate
                        ? (darkMode ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700')
                        : (darkMode ? 'bg-rose-900/50 text-rose-400' : 'bg-rose-100 text-rose-700')
                    }`}>
                      {exchangeRate > cashSummary.avgCostRate ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </div>

              {/* Net Investment */}
              <div className={`rounded-2xl p-3.5 border ${darkMode ? 'bg-slate-700/40 border-slate-600/50' : 'bg-white/80 border-slate-200/60 shadow-sm'}`}>
                <div className={`text-[9px] font-bold uppercase tracking-wider mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'en' ? 'Net Invested' : 'เงินลงทุนสุทธิ'}
                </div>
                <div className={`text-lg font-black tabular-nums tracking-tight ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  ฿{(cashSummary.totalDepositTHB - cashSummary.totalWithdrawTHB).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
                <div className={`text-[10px] font-semibold tabular-nums mt-0.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  → ${cashSummary.netDepositUSD.toLocaleString('en-US', { maximumFractionDigits: 2 })}
                </div>
              </div>

              {/* FX P&L */}
              <div className={`rounded-2xl p-3.5 border ${cashSummary.totalFxPnL >= 0
                ? (darkMode ? 'bg-emerald-900/20 border-emerald-700/40' : 'bg-emerald-50/80 border-emerald-200/60 shadow-sm')
                : (darkMode ? 'bg-rose-900/20 border-rose-700/40' : 'bg-rose-50/80 border-rose-200/60 shadow-sm')
              }`}>
                <div className={`text-[9px] font-bold uppercase tracking-wider mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {language === 'en' ? 'FX P&L' : 'กำไร/ขาดทุน FX'}
                </div>
                <div className={`text-lg font-black tabular-nums tracking-tight ${cashSummary.totalFxPnL >= 0
                  ? (darkMode ? 'text-emerald-400' : 'text-emerald-600')
                  : (darkMode ? 'text-rose-400' : 'text-rose-600')
                }`}>
                  {cashSummary.totalFxPnL >= 0 ? '+' : '-'}฿{Math.abs(cashSummary.totalFxPnL).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
                <div className={`text-[10px] font-bold tabular-nums mt-0.5 ${cashSummary.totalFxPnL >= 0
                  ? (darkMode ? 'text-emerald-400/70' : 'text-emerald-600/80')
                  : (darkMode ? 'text-rose-400/70' : 'text-rose-600/80')
                }`}>
                  ({cashSummary.totalFxPnL >= 0 ? '+' : ''}{cashSummary.fxPnLPercent.toFixed(2)}%)
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Realized PnL Detail Popup */}
      {showRealizedDetail && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setShowRealizedDetail(false)}>
          <div className="absolute inset-0 bg-black/10 backdrop-blur-[3px]" />
          <div
            className={`relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl ${darkMode ? 'bg-gradient-to-br from-slate-800 via-slate-800 to-emerald-900/20 border border-slate-700/60' : 'bg-gradient-to-br from-white via-white to-emerald-50/50 border border-slate-200'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-5 pt-5 pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`p-2 rounded-xl ${darkMode ? 'bg-gradient-to-br from-emerald-600/30 to-teal-600/20 text-emerald-400 border border-emerald-500/30' : 'bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 border border-emerald-100'}`}>
                    <TrendingUp size={18} />
                  </div>
                  <div>
                    <h3 className={`text-sm font-black tracking-tight ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                      {language === 'en' ? 'Realized P&L' : 'กำไร/ขาดทุนที่ขายแล้ว'}
                    </h3>
                    <p className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      {language === 'en' ? 'Profit from sold assets' : 'กำไรจากสินทรัพย์ที่ขายแล้ว'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRealizedDetail(false)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${darkMode ? 'bg-slate-700/60 text-slate-400 hover:text-white' : 'bg-slate-100 text-slate-400 hover:text-slate-700'}`}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Total */}
            <div className={`mx-5 mb-3 px-4 py-3 rounded-2xl border ${darkMode ? 'bg-slate-700/30 border-slate-600/40' : 'bg-slate-50 border-slate-100'}`}>
              <div className={`text-[9px] font-bold uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {language === 'en' ? 'Total Realized' : 'รวมทั้งหมด'}
              </div>
              <div className={`text-2xl font-black tabular-nums tracking-tight ${dashboardData.totalRealizedPnL >= 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-rose-400' : 'text-rose-600')}`}>
                {dashboardData.totalRealizedPnL >= 0 ? '+' : ''}{formatCurrency(dashboardData.totalRealizedPnL)}
              </div>
            </div>

            {/* Per-asset-class subtotals */}
            {(() => {
              const realizedH = holdings.filter(h => h.realizedPnLUSD && h.realizedPnLUSD !== 0);
              const classTotals = {};
              realizedH.forEach(h => {
                const cls = h.assetClass || 'Other';
                classTotals[cls] = (classTotals[cls] || 0) + (h.realizedPnLUSD || 0);
              });
              const sorted = Object.entries(classTotals).sort((a, b) => b[1] - a[1]);
              if (sorted.length === 0) return null;
              return (
                <div className="mx-5 mb-3 flex flex-wrap gap-2">
                  {sorted.map(([cls, total]) => (
                    <div key={cls} className={`px-3 py-1.5 rounded-xl border ${darkMode ? 'bg-slate-700/20 border-slate-600/30' : 'bg-slate-50 border-slate-100'}`}>
                      <div className={`text-[9px] font-bold uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{getAssetClassLabel(cls, language)}</div>
                      <div className={`text-xs font-black tabular-nums ${total >= 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-rose-400' : 'text-rose-600')}`}>
                        {total >= 0 ? '+' : ''}{formatCurrency(total)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Per-asset breakdown */}
            <div className="px-5 pb-5 max-h-[50vh] overflow-y-auto">
              <div className="space-y-0.5">
                {holdings
                  .filter(h => h.realizedPnLUSD && h.realizedPnLUSD !== 0)
                  .sort((a, b) => (b.realizedPnLUSD || 0) - (a.realizedPnLUSD || 0))
                  .map((h, idx) => (
                    <div key={idx} className={`flex items-center justify-between py-2.5 px-3 rounded-xl ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'} transition-colors`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <StockLogo symbol={h.symbol} logos={logos} size={28} />
                        <div className="min-w-0">
                          <div className={`text-xs font-bold truncate ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{h.symbol}</div>
                          <div className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{getAssetClassLabel(h.assetClass, language)}</div>
                        </div>
                      </div>
                      <div className={`text-sm font-black tabular-nums tracking-tight ${(h.realizedPnLUSD || 0) >= 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-rose-400' : 'text-rose-600')}`}>
                        {(h.realizedPnLUSD || 0) >= 0 ? '+' : ''}{formatCurrency(h.realizedPnLUSD || 0)}
                      </div>
                    </div>
                  ))
                }
                {holdings.filter(h => h.realizedPnLUSD && h.realizedPnLUSD !== 0).length === 0 && (
                  <div className={`text-center py-6 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {language === 'en' ? 'No realized trades yet' : 'ยังไม่มีรายการขาย'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Dividend Detail Popup */}
      {showDividendDetail && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={() => setShowDividendDetail(false)}>
          <div className="absolute inset-0 bg-black/10 backdrop-blur-[3px]" />
          <div
            className={`relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl ${darkMode ? 'bg-gradient-to-br from-slate-800 via-slate-800 to-amber-900/20 border border-slate-700/60' : 'bg-gradient-to-br from-white via-white to-amber-50/50 border border-slate-200'}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-5 pt-5 pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`p-2 rounded-xl ${darkMode ? 'bg-gradient-to-br from-amber-600/30 to-yellow-600/20 text-amber-400 border border-amber-500/30' : 'bg-gradient-to-br from-amber-50 to-yellow-50 text-amber-600 border border-amber-100'}`}>
                    <Coins size={18} />
                  </div>
                  <div>
                    <h3 className={`text-sm font-black tracking-tight ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                      {language === 'en' ? 'Total Dividends' : 'เงินปันผลรวม'}
                    </h3>
                    <p className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      {language === 'en' ? 'Dividends received from assets' : 'เงินปันผลที่ได้รับจากสินทรัพย์'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDividendDetail(false)}
                  className={`w-8 h-8 flex items-center justify-center rounded-full transition-colors ${darkMode ? 'bg-slate-700/60 text-slate-400 hover:text-white' : 'bg-slate-100 text-slate-400 hover:text-slate-700'}`}
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Total */}
            <div className={`mx-5 mb-3 px-4 py-3 rounded-2xl border ${darkMode ? 'bg-slate-700/30 border-slate-600/40' : 'bg-slate-50 border-slate-100'}`}>
              <div className={`text-[9px] font-bold uppercase tracking-wider mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {language === 'en' ? 'Total Received' : 'รวมทั้งหมด'}
              </div>
              <div className={`text-2xl font-black tabular-nums tracking-tight ${dashboardData.totalDividends > 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-slate-400' : 'text-slate-600')}`}>
                {dashboardData.totalDividends > 0 ? '+' : ''}{formatCurrency(dashboardData.totalDividends)}
              </div>
            </div>

            {/* Per-asset-class subtotals */}
            {(() => {
              const divHoldings = holdings.filter(h => h.totalDividendUSD && h.totalDividendUSD > 0);
              const classTotals = {};
              divHoldings.forEach(h => {
                const cls = h.assetClass || 'Other';
                classTotals[cls] = (classTotals[cls] || 0) + (h.totalDividendUSD || 0);
              });
              const sorted = Object.entries(classTotals).sort((a, b) => b[1] - a[1]);
              if (sorted.length === 0) return null;
              return (
                <div className="mx-5 mb-3 flex flex-wrap gap-2">
                  {sorted.map(([cls, total]) => (
                    <div key={cls} className={`px-3 py-1.5 rounded-xl border ${darkMode ? 'bg-slate-700/20 border-slate-600/30' : 'bg-slate-50 border-slate-100'}`}>
                      <div className={`text-[9px] font-bold uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{getAssetClassLabel(cls, language)}</div>
                      <div className={`text-xs font-black tabular-nums ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        +{formatCurrency(total)}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Per-asset breakdown */}
            <div className="px-5 pb-5 max-h-[50vh] overflow-y-auto">
              <div className="space-y-0.5">
                {holdings
                  .filter(h => h.totalDividendUSD && h.totalDividendUSD > 0)
                  .sort((a, b) => (b.totalDividendUSD || 0) - (a.totalDividendUSD || 0))
                  .map((h, idx) => (
                    <div key={idx} className={`flex items-center justify-between py-2.5 px-3 rounded-xl ${darkMode ? 'hover:bg-slate-700/30' : 'hover:bg-slate-50'} transition-colors`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <StockLogo symbol={h.symbol} logos={logos} size={28} />
                        <div className="min-w-0">
                          <div className={`text-xs font-bold truncate ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>{h.symbol}</div>
                          <div className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{getAssetClassLabel(h.assetClass, language)}</div>
                        </div>
                      </div>
                      <div className={`text-sm font-black tabular-nums tracking-tight ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                        +{formatCurrency(h.totalDividendUSD || 0)}
                      </div>
                    </div>
                  ))
                }
                {holdings.filter(h => h.totalDividendUSD && h.totalDividendUSD > 0).length === 0 && (
                  <div className={`text-center py-6 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {language === 'en' ? 'No dividends received yet' : 'ยังไม่ได้รับเงินปันผล'}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardTab;
